// apps/frontend/src/components/channels/ChannelTestDialog.tsx

import { useEffect, useState } from 'react';

import * as Dialog from '@radix-ui/react-dialog';
import { Mic, Monitor, PhoneOff, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useChannelSession } from '@/hooks/useChannelSession';
import { configurationsApi } from '@/lib/api/configurations.api';
import { toolsApi } from '@/lib/api/tools.api';

import { TranscriptPanel } from './TranscriptPanel';

import type { DisplayConfig } from './ToolResultDisplay';

interface ChannelTestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  configurationId?: string;
  configurationName: string;
}

function getConnectionLabel(state: string): string {
  switch (state) {
    case 'starting':
      return 'Starting session...';
    case 'connecting':
      return 'Negotiating WebRTC...';
    case 'connected':
      return 'Connected';
    case 'ending':
      return 'Ending session...';
    case 'ended':
      return 'Ended';
    case 'error':
      return 'Error';
    case 'idle':
    default:
      return 'Idle';
  }
}

export const ChannelTestDialog = ({
  open,
  onOpenChange,
  configurationId,
  configurationName,
}: ChannelTestDialogProps) => {
  const [toolDisplays, setToolDisplays] = useState<Record<string, DisplayConfig>>({});

  const { connectionState, timeline, error, callId, start, stop, clearTimeline, isActive } =
    useChannelSession();

  useEffect(() => {
    if (!open || !configurationId) return;
    let cancelled = false;

    (async () => {
      try {
        const config = await configurationsApi.getConfiguration(configurationId);
        if (cancelled || !config.custom_tool_ids?.length) return;

        const tools = await Promise.all(
          config.custom_tool_ids.map((id) => toolsApi.getCustomTool(id).catch(() => null)),
        );

        const displays: Record<string, DisplayConfig> = {};
        for (const tool of tools) {
          if (tool?.display_config) {
            displays[tool.name] = tool.display_config as DisplayConfig;
          }
        }
        if (!cancelled) setToolDisplays(displays);
      } catch {
        // Non-critical — tool results will fall back to raw JSON
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, configurationId]);

  useEffect(() => {
    if (!open && connectionState !== 'idle' && connectionState !== 'ended') {
      void stop();
    }
  }, [connectionState, open, stop]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60" />
        <Dialog.Content className="bg-background fixed top-1/2 left-1/2 z-50 flex h-[80vh] w-[95vw] max-w-4xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg p-6 shadow-lg">
          <div className="mb-4">
            <Dialog.Title className="text-lg font-semibold">Channel Test</Dialog.Title>
            <Dialog.Description className="text-muted-foreground mt-2 text-sm">
              Test &ldquo;{configurationName}&rdquo; directly in browser audio (no phone number).
            </Dialog.Description>
            <Dialog.Close asChild>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100"
                onClick={() => {
                  if (connectionState !== 'idle' && connectionState !== 'ended') {
                    void stop();
                  }
                  onOpenChange(false);
                }}
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </button>
            </Dialog.Close>
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-900">
              {getConnectionLabel(connectionState)}
            </span>
            {callId ? (
              <span className="rounded-full bg-cyan-100 px-2 py-1 text-xs font-medium text-cyan-900">
                Call ID: {callId.slice(0, 8)}...
              </span>
            ) : null}
          </div>

          <div className="mb-4 flex flex-wrap gap-2">
            <Button
              onClick={() => {
                if (!configurationId) return;
                void start(configurationId);
              }}
              disabled={!configurationId || isActive}
            >
              <Monitor className="mr-2 h-4 w-4" />
              Start Test
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                void stop();
              }}
              disabled={!isActive}
            >
              <PhoneOff className="mr-2 h-4 w-4" />
              End Test
            </Button>
            <Button variant="outline" onClick={clearTimeline} disabled={timeline.length === 0}>
              <Mic className="mr-2 h-4 w-4" />
              Clear Timeline
            </Button>
          </div>

          {error ? <p className="mb-3 text-sm text-red-600 dark:text-red-300">{error}</p> : null}

          <div className="bg-muted/30 flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border p-3">
            <p className="mb-3 shrink-0 text-sm font-medium">Live Timeline ({timeline.length})</p>
            <div className="min-h-0 flex-1">
              <TranscriptPanel entries={timeline} toolDisplays={toolDisplays} />
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
